import { useState } from 'react';
import {
  Dialog,
  Flex,
  Text,
  Button,
  TextField,
  Separator,
} from '@radix-ui/themes';
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { useCreateDiscordServerMutation } from '../../graphql/mutations/CreateDiscordServer.generated';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';

interface DiscordBotWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

const TOTAL_STEPS = 4;

export default function DiscordBotWizard({
  open,
  onOpenChange,
  onSuccess,
  onSkip,
}: DiscordBotWizardProps) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [serverName, setServerName] = useState('');
  const [botToken, setBotToken] = useState('');
  const [testStatus, setTestStatus] = useState<
    'idle' | 'testing' | 'success' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const [createDiscordServer, { loading: creating }] =
    useCreateDiscordServerMutation();

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTestConnection = async () => {
    if (!botToken.trim()) {
      setTestStatus('error');
      setErrorMessage('Bot token is required');
      return;
    }

    setTestStatus('testing');
    setErrorMessage('');

    // Simulate connection test (replace with actual validation later)
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // For now, accept any non-empty token
    if (botToken.trim().length > 10) {
      setTestStatus('success');
    } else {
      setTestStatus('error');
      setErrorMessage('Invalid bot token. Please check and try again.');
    }
  };

  const handleSave = async () => {
    if (testStatus !== 'success') {
      setErrorMessage('Please test the connection first');
      return;
    }

    try {
      await createDiscordServer({
        variables: {
          input: {
            name: serverName.trim(),
            ownerUserId: user?.id,
          },
        },
        refetchQueries: ['GetDiscordServers'],
      });

      toast.success(`Successfully added Discord server "${serverName.trim()}"`);

      // Reset wizard
      setCurrentStep(1);
      setServerName('');
      setBotToken('');
      setTestStatus('idle');
      setErrorMessage('');

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create Discord server';
      setErrorMessage(errorMessage);
      toast.error(`Failed to add Discord server: ${errorMessage}`);
    }
  };

  const handleSkipWizard = () => {
    onOpenChange(false);
    onSkip?.();
  };

  const handleClose = () => {
    // Reset state when closing
    setCurrentStep(1);
    setServerName('');
    setBotToken('');
    setTestStatus('idle');
    setErrorMessage('');
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content style={{ maxWidth: 600 }}>
        <Flex direction="column" gap="4">
          {/* Header */}
          <Flex justify="between" align="center">
            <Dialog.Title>
              Discord Bot Setup ({currentStep} of {TOTAL_STEPS})
            </Dialog.Title>
            <Button variant="ghost" size="1" onClick={handleSkipWizard}>
              Skip Wizard
            </Button>
          </Flex>

          {/* Progress Indicator */}
          <Flex gap="2">
            {Array.from({ length: TOTAL_STEPS }).map((_, idx) => (
              <div
                key={idx}
                style={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: idx < currentStep ? '#5865f2' : '#e5e7eb',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Flex>

          <Separator size="4" />

          {/* Step Content */}
          <div style={{ minHeight: '300px' }}>
            {currentStep === 1 && (
              <Flex direction="column" gap="4">
                <Dialog.Description>
                  Create a Discord Application to get started
                </Dialog.Description>

                <Flex direction="column" gap="3">
                  <Text weight="bold">Step 1: Create Discord Application</Text>
                  <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                    <li>
                      Go to the{' '}
                      <a
                        href="https://discord.com/developers/applications"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#5865f2',
                          textDecoration: 'underline',
                        }}
                      >
                        Discord Developer Portal
                      </a>
                    </li>
                    <li>Click "New Application" button</li>
                    <li>Give your application a name</li>
                    <li>Accept the Terms of Service and click "Create"</li>
                  </ol>

                  <details style={{ marginTop: '8px' }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                      <Text size="2" color="gray">
                        Show screenshot
                      </Text>
                    </summary>
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <Text size="2" color="gray">
                        Screenshot: Discord Developer Portal showing "New
                        Application" button in the top right
                      </Text>
                    </div>
                  </details>
                </Flex>
              </Flex>
            )}

            {currentStep === 2 && (
              <Flex direction="column" gap="4">
                <Dialog.Description>
                  Add a bot to your application and get the token
                </Dialog.Description>

                <Flex direction="column" gap="3">
                  <Text weight="bold">Step 2: Add Bot and Get Token</Text>
                  <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                    <li>In your application page, go to the "Bot" tab</li>
                    <li>Click "Add Bot" (or "Reset Token" if bot exists)</li>
                    <li>Click "Copy" to copy your bot token</li>
                    <li>Save this token - you'll need it in Step 4</li>
                  </ol>

                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '8px',
                      border: '1px solid #fbbf24',
                    }}
                  >
                    <Text size="2" weight="bold" style={{ color: '#92400e' }}>
                      ⚠️ Security Warning
                    </Text>
                    <Text
                      size="2"
                      style={{
                        color: '#78350f',
                        marginTop: '4px',
                        display: 'block',
                      }}
                    >
                      Keep your bot token secret! Never share it or commit it to
                      version control.
                    </Text>
                  </div>

                  <details style={{ marginTop: '8px' }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                      <Text size="2" color="gray">
                        Show screenshot
                      </Text>
                    </summary>
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <Text size="2" color="gray">
                        Screenshot: Bot tab showing "Reset Token" and "Copy"
                        buttons
                      </Text>
                    </div>
                  </details>
                </Flex>
              </Flex>
            )}

            {currentStep === 3 && (
              <Flex direction="column" gap="4">
                <Dialog.Description>
                  Invite the bot to your Discord server
                </Dialog.Description>

                <Flex direction="column" gap="3">
                  <Text weight="bold">Step 3: Invite Bot to Server</Text>
                  <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                    <li>Go to the "OAuth2" → "URL Generator" tab</li>
                    <li>Under "Scopes", select "bot"</li>
                    <li>Under "Bot Permissions", select these permissions:</li>
                  </ol>

                  <div style={{ marginLeft: '40px', marginTop: '8px' }}>
                    <Flex direction="column" gap="2">
                      <Text size="2">
                        ✓ <strong>Read Messages/View Channels</strong> - See
                        channels
                      </Text>
                      <Text size="2">
                        ✓ <strong>Send Messages</strong> - Send workflow
                        notifications
                      </Text>
                      <Text size="2">
                        ✓ <strong>Embed Links</strong> - Send rich embeds
                      </Text>
                    </Flex>
                  </div>

                  <ol
                    start={4}
                    style={{
                      marginLeft: '20px',
                      lineHeight: '1.8',
                      marginTop: '12px',
                    }}
                  >
                    <li>Copy the generated URL at the bottom</li>
                    <li>Open the URL in a new tab and select your server</li>
                    <li>Click "Authorize" to add the bot</li>
                  </ol>
                </Flex>
              </Flex>
            )}

            {currentStep === 4 && (
              <Flex direction="column" gap="4">
                <Dialog.Description>
                  Configure your Discord server in ChainTracks
                </Dialog.Description>

                <Flex direction="column" gap="3">
                  <label>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Server Name
                    </Text>
                    <TextField.Root
                      placeholder="My Discord Server"
                      value={serverName}
                      onChange={(e) => setServerName(e.target.value)}
                      disabled={creating}
                    />
                  </label>

                  <label>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Bot Token
                    </Text>
                    <TextField.Root
                      type="password"
                      placeholder="Paste your bot token here"
                      value={botToken}
                      onChange={(e) => {
                        setBotToken(e.target.value);
                        setTestStatus('idle');
                        setErrorMessage('');
                      }}
                      disabled={creating}
                    />
                  </label>

                  <Button
                    onClick={handleTestConnection}
                    disabled={
                      !botToken.trim() || testStatus === 'testing' || creating
                    }
                    variant="soft"
                  >
                    {testStatus === 'testing'
                      ? 'Testing...'
                      : 'Test Connection'}
                  </Button>

                  {testStatus === 'success' && (
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '8px',
                        border: '1px solid #10b981',
                      }}
                    >
                      <Text size="2" weight="bold" style={{ color: '#065f46' }}>
                        ✓ Connection successful!
                      </Text>
                    </div>
                  )}

                  {testStatus === 'error' && (
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#fee2e2',
                        borderRadius: '8px',
                        border: '1px solid #ef4444',
                      }}
                    >
                      <Text size="2" weight="bold" style={{ color: '#991b1b' }}>
                        ✗ {errorMessage || 'Connection failed'}
                      </Text>
                    </div>
                  )}
                </Flex>
              </Flex>
            )}
          </div>

          {/* Footer */}
          <Separator size="4" />
          <Flex gap="3" justify="end">
            {currentStep > 1 && (
              <Button variant="soft" onClick={handleBack} disabled={creating}>
                <ArrowLeftIcon /> Back
              </Button>
            )}

            {currentStep < TOTAL_STEPS && (
              <Button onClick={handleNext}>
                Next <ArrowRightIcon />
              </Button>
            )}

            {currentStep === TOTAL_STEPS && (
              <Button
                onClick={handleSave}
                disabled={testStatus !== 'success' || creating}
              >
                {creating ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
